import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { createEditor } from 'slate';
import { Slate, Editable, withReact } from 'slate-react';
import { notificationAPI } from '../../../services/api';
import './Notification.css';

// Slate 초기 값 (빈 단락) - 컴포넌트 외부로 이동하여 상수로 관리
const initialValue = [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

const Notification = ({ user }) => {
  const isAdmin = user?.role === 'ADMIN';
  const [notices, setNotices] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0); // 백엔드는 0부터 시작
  const itemsPerPage = 10;
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [isWriting, setIsWriting] = useState(false);
  const [editingNoticeId, setEditingNoticeId] = useState(null);
  const [expandedNotices, setExpandedNotices] = useState(new Set());
  const [writeForm, setWriteForm] = useState({
    title: '',
    content: ''
  });

  // Slate 에디터 초기화
  const editor = useMemo(() => withReact(createEditor()), []);
  
  const [editorValue, setEditorValue] = useState(initialValue);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const colorPickerRef = useRef(null);

  // 외부 클릭 시 색상 팔레트 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (colorPickerRef.current && !colorPickerRef.current.contains(event.target)) {
        setShowColorPicker(false);
      }
    };

    if (showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColorPicker]);

  // Slate 커스텀 에디터 확장 (toggleMark, toggleBlock 추가)
  const isMarkActive = (format) => {
    const marks = editor.getMarks();
    return marks ? marks[format] === true : false;
  };

  const toggleMark = (format) => {
    const isActive = isMarkActive(format);
    if (isActive) {
      editor.removeMark(format);
    } else {
      editor.addMark(format, true);
    }
  };

  const isBlockActive = (format) => {
    const { selection } = editor;
    if (!selection) return false;

    const [match] = Array.from(
      editor.nodes({
        match: (n) => n.type === format,
      })
    );

    return !!match;
  };

  const toggleBlock = (format) => {
    const isActive = isBlockActive(format);
    
    // 블록 타입 변경
    if (isActive) {
      editor.setNodes({ type: 'paragraph' });
    } else {
      editor.setNodes({ type: format });
    }
  };

  // 정렬 기능
  const toggleAlign = (alignment) => {
    editor.setNodes({ 
      align: alignment 
    });
  };

  // 폰트 변경 기능
  const changeFont = (fontFamily) => {
    if (fontFamily) {
      editor.addMark('fontFamily', fontFamily);
    } else {
      editor.removeMark('fontFamily');
    }
  };

  // 폰트 크기 변경 기능
  const changeFontSize = (fontSize) => {
    if (fontSize) {
      editor.addMark('fontSize', fontSize);
    } else {
      editor.removeMark('fontSize');
    }
  };

  // 글자 색상 변경 기능
  const changeColor = (color) => {
    if (color) {
      editor.addMark('color', color);
    } else {
      editor.removeMark('color');
    }
  };

  // editor에 메서드 추가
  editor.toggleMark = toggleMark;
  editor.toggleBlock = toggleBlock;
  editor.toggleAlign = toggleAlign;
  editor.changeFont = changeFont;
  editor.changeFontSize = changeFontSize;
  editor.changeColor = changeColor;

  // 렌더링 함수
  const renderElement = useCallback((props) => {
    const { attributes, children, element } = props;
    const style = { textAlign: element.align || 'left' };
    
    switch (element.type) {
      case 'heading-one':
        return <h1 {...attributes} style={style}>{children}</h1>;
      case 'heading-two':
        return <h2 {...attributes} style={style}>{children}</h2>;
      case 'heading-three':
        return <h3 {...attributes} style={style}>{children}</h3>;
      case 'bulleted-list':
        return <ul {...attributes} style={{ listStyleType: 'disc', ...style }}>{children}</ul>;
      case 'numbered-list':
        return <ol {...attributes} style={{ listStyleType: 'decimal', ...style }}>{children}</ol>;
      default:
        return <p {...attributes} style={style}>{children}</p>;
    }
  }, []);

  const renderLeaf = useCallback((props) => {
    const { attributes, children, leaf } = props;
    let result = children;
    const style = {};
    
    if (leaf.fontFamily) {
      style.fontFamily = leaf.fontFamily;
    }
    if (leaf.fontSize) {
      style.fontSize = leaf.fontSize;
    }
    if (leaf.color) {
      style.color = leaf.color;
    }
    
    if (leaf.bold) {
      result = <strong>{result}</strong>;
    }
    if (leaf.italic) {
      result = <em>{result}</em>;
    }
    
    return <span {...attributes} style={style}>{result}</span>;
  }, []);

  const handleToggleExpand = (noticeId, e) => {
    e.stopPropagation();
    setExpandedNotices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(noticeId)) {
        newSet.delete(noticeId);
      } else {
        newSet.add(noticeId);
      }
      return newSet;
    });
  };

  const handleBackToList = () => {
    setSelectedNotice(null);
    setIsWriting(false);
    setEditingNoticeId(null);
    setWriteForm({ title: '', content: '' });
    setEditorValue(initialValue);
  };

  const handleWriteClick = () => {
    setIsWriting(true);
    setSelectedNotice(null);
    setEditingNoticeId(null);
    setWriteForm({ title: '', content: '' });
    setEditorValue(initialValue);
  };

  // HTML을 Slate 노드로 변환하는 함수
  const deserializeFromHtml = useCallback((html) => {
    if (!html || html.trim() === '') {
      return initialValue;
    }
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const body = doc.body;
    
    const nodes = [];
    
    // body의 자식 노드들을 순회
    Array.from(body.childNodes).forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent.trim();
        if (text) {
          nodes.push({
            type: 'paragraph',
            children: [{ text }]
          });
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();
        const textContent = node.textContent || '';
        const style = node.getAttribute('style') || '';
        
        // 정렬 추출
        let align = 'left';
        if (style.includes('text-align: center')) align = 'center';
        else if (style.includes('text-align: right')) align = 'right';
        
        // 스타일 추출
        const styles = {};
        const fontSizeMatch = style.match(/font-size:\s*(\d+px)/);
        if (fontSizeMatch) styles.fontSize = fontSizeMatch[1];
        
        const colorMatch = style.match(/color:\s*([^;]+)/);
        if (colorMatch) styles.color = colorMatch[1].trim();
        
        const fontFamilyMatch = style.match(/font-family:\s*([^;]+)/);
        if (fontFamilyMatch) styles.fontFamily = fontFamilyMatch[1].trim();
        
        // 자식 노드 처리
        const children = [];
        Array.from(node.childNodes).forEach(child => {
          if (child.nodeType === Node.TEXT_NODE) {
            const text = child.textContent;
            if (text) {
              children.push({ text, ...styles });
            }
          } else if (child.nodeType === Node.ELEMENT_NODE) {
            const childTag = child.tagName.toLowerCase();
            const childText = child.textContent || '';
            const childStyle = child.getAttribute('style') || '';
            
            const childStyles = { ...styles };
            const childFontSizeMatch = childStyle.match(/font-size:\s*(\d+px)/);
            if (childFontSizeMatch) childStyles.fontSize = childFontSizeMatch[1];
            
            const childColorMatch = childStyle.match(/color:\s*([^;]+)/);
            if (childColorMatch) childStyles.color = childColorMatch[1].trim();
            
            const childFontFamilyMatch = childStyle.match(/font-family:\s*([^;]+)/);
            if (childFontFamilyMatch) childStyles.fontFamily = childFontFamilyMatch[1].trim();
            
            if (childTag === 'strong' || childTag === 'b') {
              childStyles.bold = true;
            }
            if (childTag === 'em' || childTag === 'i') {
              childStyles.italic = true;
            }
            
            if (childText) {
              children.push({ text: childText, ...childStyles });
            }
          }
        });
        
        if (children.length === 0 && textContent) {
          children.push({ text: textContent, ...styles });
        }
        
        if (children.length > 0) {
          if (tagName === 'p') {
            nodes.push({
              type: 'paragraph',
              align,
              children
            });
          } else if (tagName === 'h1') {
            nodes.push({
              type: 'heading-one',
              align,
              children
            });
          } else if (tagName === 'h2') {
            nodes.push({
              type: 'heading-two',
              align,
              children
            });
          } else if (tagName === 'h3') {
            nodes.push({
              type: 'heading-three',
              align,
              children
            });
          } else {
            nodes.push({
              type: 'paragraph',
              align,
              children
            });
          }
        }
      }
    });
    
    return nodes.length > 0 ? nodes : initialValue;
  }, []);

  // 수정 버튼 클릭 핸들러
  const handleEditClick = async (noticeId, e) => {
    e.stopPropagation(); // 아코디언 토글 방지
    try {
      const notice = await notificationAPI.getById(noticeId);
      setEditingNoticeId(noticeId);
      setWriteForm({
        title: notice.title,
        content: notice.content
      });
      // HTML을 Slate 노드로 변환
      const slateNodes = deserializeFromHtml(notice.content);
      setEditorValue(slateNodes);
      setIsWriting(true);
      setSelectedNotice(null);
    } catch (error) {
      console.error('공지사항 조회 실패:', error);
      alert('공지사항을 불러오는데 실패했습니다.');
    }
  };

  // 삭제 버튼 클릭 핸들러
  const handleDeleteClick = async (noticeId, e) => {
    e.stopPropagation(); // 아코디언 토글 방지
    if (!window.confirm('정말 삭제하시겠습니까?')) {
      return;
    }
    
    try {
      await notificationAPI.delete(noticeId);
      alert('공지사항이 삭제되었습니다.');
      // 목록 새로고침
      fetchNotices(currentPage);
      // 확장된 항목에서 제거
      setExpandedNotices(prev => {
        const newSet = new Set(prev);
        newSet.delete(noticeId);
        return newSet;
      });
    } catch (error) {
      console.error('공지사항 삭제 실패:', error);
      alert('공지사항 삭제에 실패했습니다.');
    }
  };

  const handleWriteFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setWriteForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const escapeHtml = (text) => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  const serializeLeaf = useCallback((children) => {
    return children.map(child => {
      let text = escapeHtml(child.text);
      const style = [];
      if (child.fontFamily) {
        style.push(`font-family: ${child.fontFamily}`);
      }
      if (child.fontSize) {
        style.push(`font-size: ${child.fontSize}`);
      }
      if (child.color) {
        style.push(`color: ${child.color}`);
      }
      const styleAttr = style.length > 0 ? ` style="${style.join('; ')}"` : '';
      
      if (child.bold) text = `<strong>${text}</strong>`;
      if (child.italic) text = `<em>${text}</em>`;
      if (styleAttr) text = `<span${styleAttr}>${text}</span>`;
      return text;
    }).join('');
  }, []);

  // Slate 노드를 HTML로 변환하는 함수
  const serializeToHtml = useCallback((nodes) => {
    return nodes.map(node => {
      const alignStyle = node.align ? ` style="text-align: ${node.align};"` : '';
      if (node.type === 'paragraph') {
        return `<p${alignStyle}>${serializeLeaf(node.children)}</p>`;
      } else if (node.type === 'heading-one') {
        return `<h1${alignStyle}>${serializeLeaf(node.children)}</h1>`;
      } else if (node.type === 'heading-two') {
        return `<h2${alignStyle}>${serializeLeaf(node.children)}</h2>`;
      } else if (node.type === 'heading-three') {
        return `<h3${alignStyle}>${serializeLeaf(node.children)}</h3>`;
      } else if (node.type === 'bulleted-list') {
        return `<ul${alignStyle}>${node.children.map(child => `<li>${serializeLeaf(child.children)}</li>`).join('')}</ul>`;
      } else if (node.type === 'numbered-list') {
        return `<ol${alignStyle}>${node.children.map(child => `<li>${serializeLeaf(child.children)}</li>`).join('')}</ol>`;
      }
      return '';
    }).join('');
  }, [serializeLeaf]);

  // Slate 에디터 변경 핸들러
  const handleEditorChange = useCallback((value) => {
    setEditorValue(value);
    
    // 현재 선택된 텍스트의 mark 상태 확인 및 업데이트
    const marks = editor.getMarks();
    setIsBold(marks ? marks.bold === true : false);
    setIsItalic(marks ? marks.italic === true : false);
    
    // Slate 노드를 HTML 문자열로 변환하여 저장
    const html = serializeToHtml(value);
    setWriteForm(prev => ({
      ...prev,
      content: html
    }));
    
    // Placeholder 표시/숨김 처리
    setTimeout(() => {
      const isEmpty = value.length === 1 && 
                      value[0].type === 'paragraph' && 
                      value[0].children.length === 1 && 
                      value[0].children[0].text === '';
      const container = document.querySelector('.slate-editor-container');
      if (container) {
        if (isEmpty) {
          container.classList.add('placeholder');
        } else {
          container.classList.remove('placeholder');
        }
      }
    }, 0);
  }, [editor, serializeToHtml]);

  // 공지사항 목록 조회
  const fetchNotices = useCallback(async (page = 0) => {
    try {
      setLoading(true);
      const response = await notificationAPI.getAll(page, itemsPerPage);
      // Spring Page 객체에서 데이터 추출
      const noticesList = response.content || [];
      // 날짜 형식 변환 및 데이터 매핑
      const mappedNotices = noticesList.map(notice => ({
        id: notice.id,
        title: notice.title,
        content: notice.content,
        date: notice.createdAt ? notice.createdAt.split('T')[0] : '',
        author: notice.author?.name || notice.author?.username || '관리자',
        important: false // 백엔드에 important 필드가 없으면 false
      }));
      setNotices(mappedNotices);
      setTotalPages(response.totalPages || 0);
      setTotalElements(response.totalElements || 0);
    } catch (error) {
      console.error('공지사항 조회 실패:', error);
      alert('공지사항을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage]);

  // 초기 로드 및 페이지 변경 시 데이터 조회
  useEffect(() => {
    fetchNotices(currentPage);
  }, [currentPage, fetchNotices]);

  const handleWriteSubmit = async (e) => {
    e.preventDefault();
    // 내용이 비어있는지 확인 (텍스트만 확인)
    const textContent = editorValue
      .map(node => {
        if (node.children) {
          return node.children.map(child => child.text).join('');
        }
        return '';
      })
      .join('')
      .trim();
    if (!writeForm.title.trim() || !textContent) {
      alert('제목과 내용을 입력해주세요.');
      return;
    }
    
    try {
      if (editingNoticeId) {
        // 수정 모드
        await notificationAPI.update(
          editingNoticeId,
          writeForm.title,
          writeForm.content
        );
        alert('공지사항이 수정되었습니다.');
      } else {
        // 작성 모드
        await notificationAPI.save(
          writeForm.title,
          writeForm.content,
          user?.id
        );
        alert('공지사항이 작성되었습니다.');
      }
      // 폼 초기화 및 목록으로 돌아가기
      setWriteForm({ title: '', content: '' });
      setEditorValue(initialValue);
      setIsWriting(false);
      setEditingNoticeId(null);
      // 목록 새로고침
      fetchNotices(currentPage);
    } catch (error) {
      console.error('공지사항 저장 실패:', error);
      alert(editingNoticeId ? '공지사항 수정에 실패했습니다.' : '공지사항 작성에 실패했습니다.');
    }
  };

  // 현재 페이지의 공지사항 (백엔드에서 이미 페이지네이션된 데이터)
  const currentNotices = notices;

  const handlePageChange = (page) => {
    // 프론트엔드는 1부터 시작, 백엔드는 0부터 시작
    setCurrentPage(page - 1);
    // 페이지 변경 시 스크롤을 맨 위로
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 페이지 번호 배열 생성 (1부터 시작)
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5; // 최대 5개의 페이지 번호 표시
    const displayPage = currentPage + 1; // 표시용 페이지 (1부터 시작)
    
    if (totalPages <= maxPagesToShow) {
      // 전체 페이지가 5개 이하인 경우 모두 표시
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // 현재 페이지 주변의 페이지 번호만 표시
      if (displayPage <= 3) {
        // 앞부분
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
      } else if (displayPage >= totalPages - 2) {
        // 뒷부분
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // 중간
        for (let i = displayPage - 2; i <= displayPage + 2; i++) {
          pages.push(i);
        }
      }
    }
    return pages;
  };

  if (selectedNotice) {
    return (
      <div className="notice-detail-page">
        <button className="back-to-list-button" onClick={handleBackToList}>
          ← 목록으로
        </button>
        <div className="notice-detail-card">
          <div className="notice-detail-header">
            <h2 className="notice-detail-title">
              {selectedNotice.important && <span className="important-badge">중요</span>}
              {selectedNotice.title}
            </h2>
            <div className="notice-detail-meta">
              <span className="notice-date">{selectedNotice.date}</span>
              <span className="notice-author">작성자: {selectedNotice.author}</span>
            </div>
          </div>
          <div 
            className="notice-detail-content"
            dangerouslySetInnerHTML={{ __html: selectedNotice.content }}
          />
        </div>
      </div>
    );
  }

  if (isWriting) {
    return (
      <div className="notice-write-page">
        <div className="notice-write-header">
          <h1>{editingNoticeId ? '공지사항 수정' : '공지사항 작성'}</h1>
        </div>
        <form className="notice-write-form" onSubmit={handleWriteSubmit}>
          <div className="notice-write-form-group notice-write-form-group-row">
            <div className="notice-write-form-group notice-write-form-group-horizontal notice-write-title-group">
              <label htmlFor="title">제목</label>
              <input
                type="text"
                id="title"
                name="title"
                value={writeForm.title}
                onChange={handleWriteFormChange}
                placeholder="제목을 입력하세요"
                required
              />
            </div>
            <div className="notice-write-form-group notice-write-form-group-horizontal notice-write-author-group">
              <label htmlFor="author">작성자</label>
              <input
                type="text"
                id="author"
                name="author"
                value={user?.name || ''}
                readOnly
                className="notice-write-readonly-input"
              />
            </div>
          </div>
          
          <div className="notice-write-form-group">
            <div className="notice-write-editor-wrapper">
              <Slate editor={editor} initialValue={editorValue} onChange={handleEditorChange}>
                <div className="slate-toolbar">
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor.toggleMark('bold');
                    }}
                    className={`slate-toolbar-button ${isBold ? 'active' : ''}`}
                  >
                    <strong>B</strong>
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor.toggleMark('italic');
                    }}
                    className={`slate-toolbar-button ${isItalic ? 'active' : ''}`}
                  >
                    <em>I</em>
                  </button>
                  <div className="slate-toolbar-divider"></div>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor.toggleAlign('left');
                    }}
                    className="slate-toolbar-button slate-align-button"
                    title="왼쪽 정렬"
                  >
                    <div className="slate-align-icon slate-align-left">
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor.toggleAlign('center');
                    }}
                    className="slate-toolbar-button slate-align-button"
                    title="가운데 정렬"
                  >
                    <div className="slate-align-icon slate-align-center">
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </button>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor.toggleAlign('right');
                    }}
                    className="slate-toolbar-button slate-align-button"
                    title="오른쪽 정렬"
                  >
                    <div className="slate-align-icon slate-align-right">
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </button>
                  <div className="slate-toolbar-divider"></div>
                  <select
                    className="slate-toolbar-select"
                    onChange={(e) => {
                      e.preventDefault();
                      editor.changeFont(e.target.value);
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                    title="폰트 선택"
                  >
                    <option value="">폰트</option>
                    <option value="'Nanum Gothic', sans-serif">나눔고딕</option>
                    <option value="'Nanum Myeongjo', serif">나눔명조</option>
                    <option value="'Malgun Gothic', sans-serif">맑은 고딕</option>
                    <option value="'Noto Sans KR', sans-serif">Noto Sans KR</option>
                    <option value="'Noto Serif KR', serif">Noto Serif KR</option>
                    <option value="Dotum, sans-serif">돋움</option>
                    <option value="Gulim, sans-serif">굴림</option>
                    <option value="Batang, serif">바탕</option>
                    <option value="Gungsuh, serif">궁서</option>
                  </select>
                  <select
                    className="slate-toolbar-select"
                    onChange={(e) => {
                      e.preventDefault();
                      editor.changeFontSize(e.target.value);
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}
                    title="폰트 크기"
                  >
                    <option value="">크기</option>
                    <option value="10px">10px</option>
                    <option value="12px">12px</option>
                    <option value="14px">14px</option>
                    <option value="16px">16px</option>
                    <option value="18px">18px</option>
                    <option value="20px">20px</option>
                    <option value="24px">24px</option>
                    <option value="28px">28px</option>
                    <option value="32px">32px</option>
                    <option value="36px">36px</option>
                    <option value="48px">48px</option>
                  </select>
                  <div className="slate-color-picker-wrapper" ref={colorPickerRef}>
                    <button
                      type="button"
                      className="slate-toolbar-button slate-color-button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setShowColorPicker(!showColorPicker);
                      }}
                      title="글자 색상"
                    >
                      <span style={{ textDecoration: 'underline' }}>A</span>
                    </button>
                    {showColorPicker && (
                      <div className="slate-color-picker" onMouseDown={(e) => e.stopPropagation()}>
                        <div className="slate-color-grid">
                          {[
                            "#000000", "#e60000", "#ff9900", "#ffff00", "#008a00", "#0066cc", "#9933ff",
                            "#ffffff", "#facccc", "#ffebcc", "#ffffcc", "#cce8cc", "#cce0f5", "#ebd6ff",
                            "#bbbbbb", "#f06666", "#ffc266", "#ffff66", "#66b966", "#66a3e0", "#c285ff",
                            "#888888", "#a10000", "#b26b00", "#b2b200", "#006100", "#0047b2", "#6b24b2",
                            "#444444", "#5c0000", "#663d00", "#666600", "#003700", "#002966", "#3d1466"
                          ].map((color, index) => (
                            <button
                              key={index}
                              type="button"
                              className="slate-color-item"
                              style={{ backgroundColor: color }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                editor.changeColor(color);
                                setShowColorPicker(false);
                              }}
                              title={color}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="slate-editor-container placeholder" data-placeholder="내용을 입력하세요">
                  <Editable
                    renderElement={renderElement}
                    renderLeaf={renderLeaf}
                    className="slate-editor"
                  />
                </div>
              </Slate>
            </div>
          </div>

          <div className="notice-write-form-actions">
            <button type="button" className="notice-write-cancel-button" onClick={handleBackToList}>
              취소
            </button>
            <button type="submit" className="notice-write-submit-button">
              {editingNoticeId ? '수정하기' : '작성하기'}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // 번호 계산 (전체 목록 기준 역순)
  const getNoticeNumber = (index) => {
    // 전체 개수에서 현재 페이지의 인덱스를 빼서 역순으로 표시
    return totalElements - (currentPage * itemsPerPage + index);
  };

  return (
    <div className="notice-page">
      <div className="notice-header">
        <div className="notice-header-top">
          <div className="notice-header-left"></div>
          <h1 className="notice-header-center">공지사항</h1>
          <div className="notice-header-right">
            {isAdmin && (
              <button className="write-button" onClick={handleWriteClick}>
                글쓰기
              </button>
            )}
          </div>
        </div>
      </div>
      
      <div className="notice-board">
        <div className={`notice-table-wrapper ${expandedNotices.size === 0 ? 'no-scroll' : ''}`}>
          <table className="notice-table">
          <thead>
            <tr>
              <th className="col-number">번호</th>
              <th className="col-title">제목</th>
              <th className="col-author">작성자</th>
              <th className="col-date">작성일</th>
            </tr>
          </thead>
          <tbody>
            {currentNotices.length > 0 ? (
              currentNotices.map((notice, index) => {
                const isExpanded = expandedNotices.has(notice.id);
                return (
                  <React.Fragment key={notice.id}>
                    <tr 
                      className={`notice-row ${notice.important ? 'important' : ''}`}
                    >
                      <td className="col-number">
                        {notice.important ? (
                          <span className="important-mark">공지</span>
                        ) : (
                          getNoticeNumber(index)
                        )}
                      </td>
                      <td 
                        className={`col-title ${isExpanded ? 'expanded' : ''}`}
                        onClick={(e) => handleToggleExpand(notice.id, e)}
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="notice-title-text">
                          {notice.important && <span className="important-badge">중요공지</span>}
                          {notice.title}
                        </span>
                      </td>
                      <td className="col-author">{notice.author}</td>
                      <td className="col-date">{notice.date}</td>
                    </tr>
                    {isExpanded && (
                      <tr className="notice-content-row">
                        <td colSpan="4" className="notice-content-cell">
                          <div className="notice-content-wrapper">
                            <div 
                              className="notice-content-text"
                              dangerouslySetInnerHTML={{ __html: notice.content }}
                            />
                            {isAdmin && (
                              <div className="notice-content-actions">
                                <button
                                  className="notice-edit-button"
                                  onClick={(e) => handleEditClick(notice.id, e)}
                                >
                                  수정
                                </button>
                                <button
                                  className="notice-delete-button"
                                  onClick={(e) => handleDeleteClick(notice.id, e)}
                                >
                                  삭제
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan="5" className="no-notices-cell">
                  등록된 공지사항이 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* 페이지네이션 */}
      {!loading && notices.length > 0 && totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination-button"
            onClick={() => handlePageChange(currentPage)}
            disabled={currentPage === 0}
          >
            이전
          </button>
          
          <div className="pagination-numbers">
            {currentPage + 1 > 3 && totalPages > 5 && (
              <>
                <button
                  className="pagination-number"
                  onClick={() => handlePageChange(1)}
                >
                  1
                </button>
                {currentPage + 1 > 4 && <span className="pagination-ellipsis">...</span>}
              </>
            )}
            
            {getPageNumbers().map((page) => (
              <button
                key={page}
                className={`pagination-number ${currentPage + 1 === page ? 'active' : ''}`}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}
            
            {currentPage + 1 < totalPages - 2 && totalPages > 5 && (
              <>
                {currentPage + 1 < totalPages - 3 && <span className="pagination-ellipsis">...</span>}
                <button
                  className="pagination-number"
                  onClick={() => handlePageChange(totalPages)}
                >
                  {totalPages}
                </button>
              </>
            )}
          </div>
          
          <button
            className="pagination-button"
            onClick={() => handlePageChange(currentPage + 2)}
            disabled={currentPage + 1 >= totalPages}
          >
            다음
          </button>
        </div>
      )}
      
      {/* 로딩 표시 */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          로딩 중...
        </div>
      )}
      
      {/* 데이터 없음 표시 */}
      {!loading && notices.length === 0 && (
        <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8' }}>
          등록된 공지사항이 없습니다.
        </div>
      )}
    </div>
  );
};

export default Notification;

